import { computed, ref, watch, onBeforeUnmount, toValue, type Ref } from 'vue';
import { ibWS } from '@ibgroup-tws-webapps/ib.utils';
import { useUserState } from '@/stores/useUserState';
import { usePriceSideSetting } from '@/composables/usePriceSideSetting';
import type { CandidateTableRow } from '../types/election.types';

interface UseVisibilityStreamedMdDataOptions {
  visibleCandidatesByRace: Ref<Map<string, CandidateTableRow[]>>;
  currentlyVisibleRaceIds: Ref<Set<string>>;
  yesConidExPairsMap: Ref<Record<number, string[]>>;
  noConidExPairsMap: Ref<Record<number, string[]>>;
}

interface ActiveSub {
  requestId: string;
  subscription: { unsubscribe(): void };
}

async function makeRequestId(suffix: string): Promise<string> {
  const webAppId = await ibWS.getWebAppId();
  return `vis-md-${suffix}-${webAppId}`;
}

/**
 * Live MD data via WebSocket for the election grid.
 *
 * Auth: streams flexbest-bid-ask (YES + NO) and open-interest per visible candidate.
 * Non-auth: returns empty maps — seeds from the grid API are used directly.
 */
export function useVisibilityStreamedMdData({
  visibleCandidatesByRace,
  currentlyVisibleRaceIds,
  yesConidExPairsMap,
  noConidExPairsMap,
}: UseVisibilityStreamedMdDataOptions) {
  const userState = useUserState();
  const shouldStream = computed(() => !toValue(userState.usePublicApi));
  const { priceSide } = usePriceSideSetting();

  const liveBidMap = ref<Record<number, number | null>>({});
  const liveOiMap = ref<Record<number, number | undefined>>({});

  const activeSubs = new Map<string, ActiveSub>();

  async function subscribeBidAsk(conid: number, conidExPairs: string[]) {
    const key = `bid-${conid}`;
    if (activeSubs.has(key)) return;

    const requestId = await makeRequestId(`bid-${conid}`);
    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: { destination: '/event-contract/flexbest-bid-ask', 'x-target': 'mdfarm', id: requestId },
        body: { conid_exs: conidExPairs },
      },
    });

    const observable = ibWS.receive(requestId);
    if (!observable) return;

    const subscription = observable.subscribe({
      /* eslint-disable @typescript-eslint/no-explicit-any */
      next: (msg: any) => {
        const body = msg?.payload ?? msg?.body ?? msg;
        if (!body) return;
        const side = priceSide.value;
        const val = body[side];
        if (typeof val !== 'undefined') {
          const num = Number(val);
          if (!isNaN(num) && liveBidMap.value[conid] !== num) {
            liveBidMap.value[conid] = num;
          }
        }
      },
    });

    activeSubs.set(key, { requestId, subscription });
  }

  async function subscribeOi(conidYes: number, exchange: string) {
    const key = `oi-${conidYes}`;
    if (activeSubs.has(key)) return;

    const requestId = await makeRequestId(`oi-${conidYes}`);
    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: { destination: '/event-contract/open-interest', 'x-target': 'mdfarm', id: requestId },
        body: { conid: conidYes, exchange },
      },
    });

    const observable = ibWS.receive(requestId);
    if (!observable) return;

    const subscription = observable.subscribe({
      /* eslint-disable @typescript-eslint/no-explicit-any */
      next: (msg: any) => {
        const body = msg?.payload ?? msg?.body ?? msg;
        if (!body) return;
        const put = typeof body.putInterest !== 'undefined' ? Number(body.putInterest) : 0;
        const call = typeof body.callInterest !== 'undefined' ? Number(body.callInterest) : 0;
        const total = put + call;
        if (liveOiMap.value[conidYes] !== total) {
          liveOiMap.value[conidYes] = total;
        }
      },
    });

    activeSubs.set(key, { requestId, subscription });
  }

  function unsubscribeByKey(key: string) {
    const sub = activeSubs.get(key);
    if (!sub) return;
    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: { destination: '/md/stream/unsubscribe', 'x-target': 'mdfarm', id: sub.requestId },
      },
    });
    sub.subscription.unsubscribe();
    activeSubs.delete(key);
  }

  function unsubscribeAll() {
    for (const key of Array.from(activeSubs.keys())) {
      unsubscribeByKey(key);
    }
  }

  /**
   * Stable key derived from the visible conid set.
   * Only changes when actual conid membership changes.
   */
  const visibleConidKey = computed(() => {
    const visible = currentlyVisibleRaceIds.value;
    const conids: number[] = [];
    visibleCandidatesByRace.value.forEach((candidates, raceId) => {
      if (!visible.has(raceId)) return;
      for (const c of candidates) {
        conids.push(c.conid_yes, c.conid_no);
      }
    });
    return conids.sort((a, b) => a - b).join(',');
  });

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  function reconcileSubscriptions() {
    if (!shouldStream.value) return;

    const visible = currentlyVisibleRaceIds.value;
    const neededKeys = new Set<string>();

    visibleCandidatesByRace.value.forEach((candidates, raceId) => {
      if (!visible.has(raceId)) return;
      for (const c of candidates) {
        const yesPairs = yesConidExPairsMap.value[c.conid_yes] ?? [`${c.conid_yes}@${c.exchange}`];
        const noPairs = noConidExPairsMap.value[c.conid_yes] ?? [`${c.conid_no}@${c.exchange}`];

        const yesKey = `bid-${c.conid_yes}`;
        const noKey = `bid-${c.conid_no}`;
        const oiKey = `oi-${c.conid_yes}`;

        neededKeys.add(yesKey);
        neededKeys.add(noKey);
        neededKeys.add(oiKey);

        subscribeBidAsk(c.conid_yes, yesPairs);
        subscribeBidAsk(c.conid_no, noPairs);
        subscribeOi(c.conid_yes, c.exchange);
      }
    });

    // Unsubscribe anything no longer needed
    for (const key of Array.from(activeSubs.keys())) {
      if (!neededKeys.has(key)) {
        unsubscribeByKey(key);
      }
    }
  }

  // Reset all accumulated state when the underlying dataset changes
  const raceKeySet = computed(() => {
    const keys: string[] = [];
    visibleCandidatesByRace.value.forEach((_, raceId) => keys.push(raceId));
    return keys.sort().join(',');
  });

  watch(raceKeySet, () => {
    unsubscribeAll();
    liveBidMap.value = {};
    liveOiMap.value = {};
  });

  // Reconcile subscriptions when visible conids change (debounced)
  watch(
    visibleConidKey,
    (key) => {
      clearTimeout(debounceTimer);
      if (!shouldStream.value || !key) {
        unsubscribeAll();
        return;
      }
      debounceTimer = setTimeout(reconcileSubscriptions, 150);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    clearTimeout(debounceTimer);
    unsubscribeAll();
  });

  return { liveBidMap, liveOiMap };
}

// Keep old name as alias for backwards compat during transition
export const useVisibilityPolledMdData = useVisibilityStreamedMdData;
